/**
 * Secure Storage Utility for Authentication Tokens
 * Implements best practices for storing sensitive authentication data
 */

// Simple encryption/decryption for additional security layer
const STORAGE_KEY_PREFIX = "gargle_auth_";
const ENCRYPTION_KEY = "gargle_analytics_2024"; // In production, this should be environment-specific
const isDevelopment = process.env.NODE_ENV === "development";

// Simple XOR encryption for basic obfuscation
const encrypt = (text) => {
  if (!text) return "";

  let result = "";
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(
      text.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length)
    );
  }
  return btoa(result); // Base64 encode
};

const decrypt = (encryptedText) => {
  if (!encryptedText) return "";

  try {
    const text = atob(encryptedText); // Base64 decode
    let result = "";
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(
        text.charCodeAt(i) ^
          ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length)
      );
    }
    return result;
  } catch (error) {
    if (!isDevelopment && window.errorReporting) {
      window.errorReporting.captureException(error);
    }
    return "";
  }
};

// Check if storage is available
const isStorageAvailable = (type) => {
  try {
    const storage = window[type];
    const test = "__storage_test__";
    storage.setItem(test, test);
    storage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
};

// Secure storage implementation
export const secureStorage = {
  // Store encrypted data
  setItem: (key, value) => {
    try {
      if (!isStorageAvailable("localStorage")) {
        if (!isStorageAvailable("sessionStorage")) {
          if (!isDevelopment && window.errorReporting) {
            window.errorReporting.captureException(
              new Error("No storage available")
            );
          }
          return false;
        }
        sessionStorage.setItem(
          STORAGE_KEY_PREFIX + key,
          encrypt(JSON.stringify(value))
        );
        return true;
      }

      localStorage.setItem(
        STORAGE_KEY_PREFIX + key,
        encrypt(JSON.stringify(value))
      );
      return true;
    } catch (error) {
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureException(error);
      }
      return false;
    }
  },

  // Retrieve and decrypt data
  getItem: (key) => {
    try {
      let encryptedData = null;

      if (isStorageAvailable("localStorage")) {
        encryptedData = localStorage.getItem(STORAGE_KEY_PREFIX + key);
      }

      if (!encryptedData && isStorageAvailable("sessionStorage")) {
        encryptedData = sessionStorage.getItem(STORAGE_KEY_PREFIX + key);
      }

      if (!encryptedData) return null;

      const decryptedData = decrypt(encryptedData);
      return JSON.parse(decryptedData);
    } catch (error) {
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureException(error);
      }
      return null;
    }
  },

  // Remove data from both storages
  removeItem: (key) => {
    try {
      if (isStorageAvailable("localStorage")) {
        localStorage.removeItem(STORAGE_KEY_PREFIX + key);
      }
      if (isStorageAvailable("sessionStorage")) {
        sessionStorage.removeItem(STORAGE_KEY_PREFIX + key);
      }
      return true;
    } catch (error) {
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureException(error);
      }
      return false;
    }
  },

  // Clear all auth-related data
  clear: () => {
    try {
      const keys = ["tokens", "user", "session"];
      keys.forEach((key) => secureStorage.removeItem(key));
      return true;
    } catch (error) {
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureException(error);
      }
      return false;
    }
  },
};

// Token-specific storage functions
export const tokenStorage = {
  // Store authentication tokens
  setTokens: (tokens) => {
    if (!tokens || !tokens.accessToken) {
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureException(
          new Error("Invalid tokens provided")
        );
      }
      return false;
    }

    const tokenData = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      timestamp: Date.now(),
      expiresAt: tokens.expiresAt || Date.now() + 60 * 60 * 1000, // Default 1 hour
    };

    return secureStorage.setItem("tokens", tokenData);
  },

  // Retrieve authentication tokens
  getTokens: () => {
    const tokenData = secureStorage.getItem("tokens");

    if (!tokenData) return null;

    // Check if tokens are expired
    if (tokenData.expiresAt && Date.now() > tokenData.expiresAt) {
      if (!isDevelopment && window.errorReporting) {
        window.errorReporting.captureException(
          new Error("Stored tokens have expired")
        );
      }
      tokenStorage.clearTokens();
      return null;
    }

    return {
      accessToken: tokenData.accessToken,
      refreshToken: tokenData.refreshToken,
      expiresAt: tokenData.expiresAt,
    };
  },

  // Clear authentication tokens
  clearTokens: () => {
    return secureStorage.removeItem("tokens");
  },

  // Check if valid tokens exist
  hasValidTokens: () => {
    const tokens = tokenStorage.getTokens();
    return tokens && tokens.accessToken;
  },
};

// User data storage
export const userStorage = {
  // Store user information
  setUser: (user) => {
    if (!user) return false;

    const userData = {
      ...user,
      timestamp: Date.now(),
    };

    return secureStorage.setItem("user", userData);
  },

  // Retrieve user information
  getUser: () => {
    return secureStorage.getItem("user");
  },

  // Clear user information
  clearUser: () => {
    return secureStorage.removeItem("user");
  },
};

// Session management
export const sessionManager = {
  // Store session data
  setSession: (sessionData) => {
    const session = {
      ...sessionData,
      timestamp: Date.now(),
      expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
    };

    return secureStorage.setItem("session", session);
  },

  // Get session data
  getSession: () => {
    const session = secureStorage.getItem("session");

    if (!session) return null;

    // Check if session is expired
    if (session.expiresAt && Date.now() > session.expiresAt) {
      sessionManager.clearSession();
      return null;
    }

    return session;
  },

  // Clear session data
  clearSession: () => {
    return secureStorage.removeItem("session");
  },

  // Check if valid session exists
  hasValidSession: () => {
    const session = sessionManager.getSession();
    return session && session.timestamp;
  },
};

// Complete cleanup function
export const clearAllAuthData = () => {
  try {
    tokenStorage.clearTokens();
    userStorage.clearUser();
    sessionManager.clearSession();
    secureStorage.clear();

    // Also clear any return URL
    if (isStorageAvailable("sessionStorage")) {
      window.sessionStorage.removeItem("auth_return_url");
    }

    return true;
  } catch (error) {
    if (!isDevelopment && window.errorReporting) {
      window.errorReporting.captureException(error);
    }
    return false;
  }
};

export default {
  secureStorage,
  tokenStorage,
  userStorage,
  sessionManager,
  clearAllAuthData,
};
